/**
 * Circling Radius Computation — src/lib/circlingRadius.ts
 *
 * Computes the circling radius (in NM) for a given aircraft category at a
 * given approach plate, using the FAA AIM Para 5-4-20 Table 5-4-29 unified
 * circling-approach maneuvering-radius table.
 *
 * Axis: Circling MDA in feet **MSL** (NOT height above ground). The previous
 * implementation keyed off HAG (MDA − airport elevation); that was incorrect
 * and produced wrong-row lookups at high-elevation airports (e.g. KASE 7820 ft,
 * KTEX 9078 ft) where HAG and MSL diverge by thousands of feet.
 *
 * FAA Aeronautical Information Services Charting Notice TERM 25-05 (May 14
 * 2025) announced removal of the inverse-C "expanded circling" icon from
 * approach plates beginning with the August 7, 2025 publication cycle. The
 * standard/expanded distinction is deprecated — this single table now applies
 * to all approaches. See:
 *   - https://www.faa.gov/air_traffic/flight_info/aeronav/safety_alerts/media/TERM_25-05_CN_Removal_of_Circling_Icon.pdf
 *   - https://www.faa.gov/air_traffic/publications/atpubs/aim_html/chap5_section_4.html
 *
 * Safety contract:
 *   - circlingMdaFt null → use the lowest band (≤1000 ft MSL) as a
 *     conservative fallback. Under-claims radius; never over-claims.
 *   - airportElevFt is informational only (used to compute AGL for display).
 *     It NEVER affects the table lookup.
 */

// ─── Types ───────────────────────────────────────────────────────────────────

/** FAA approach-speed / performance category. */
export type ApproachCategory = 'A' | 'B' | 'C' | 'D' | 'E';

export interface CirclingRadiusInput {
  /** FAA approach-speed category of the aircraft. */
  category: ApproachCategory;
  /** Circling MDA in feet MSL (from cifp_approach_minima.circling_mda_X). */
  circlingMdaFt: number | null;
  /** Airport field elevation (MSL ft), from NASR. Used only for the AGL display value, NOT for table lookup. */
  airportElevFt: number;
}

export interface CirclingRadiusResult {
  /** Circling radius in nautical miles. */
  radiusNm: number;
  /** MDA-MSL band label, e.g. "1001–3000 ft MSL". For display. */
  bandLabel: string;
  /** MDA AGL for display ("MDA: 1240 ft (440 AGL)"). NOT used for lookup. */
  agl: number | null;
  /** True if we used a fallback (null MDA). */
  isFallback: boolean;
}

// ─── Legacy standard TERPS table (pre-2012, historical reference only) ───────

/**
 * Legacy "standard TERPS" circling radii (NM) by category — fixed, no altitude
 * dependency. NOT used in computation any more. Retained for historical
 * reference and audit. Approach designs published before the FAA expanded-
 * criteria program (and unchanged since) used these values; the FAA TERM
 * 25-05 charting notice has since removed the inverse-C distinction so this
 * table no longer represents any active operational regime.
 *
 * Use `CIRCLING_RADII_NM` for all current radius computations.
 */
export const LEGACY_STANDARD_RADII_NM: Record<ApproachCategory, number> = {
  A: 1.3,
  B: 1.5,
  C: 1.7,
  D: 2.3,
  E: 4.5,
};

// ─── Unified circling-radius table — AIM 5-4-20 Table 5-4-29 ────────────────
//
// Rows = aircraft approach category.
// Columns = circling MDA altitude band, in feet **MSL**:
//   [0] ≤1000   [1] 1001–3000   [2] 3001–5000
//   [3] 5001–7000   [4] 7001–9000   [5] ≥9001
//
// Source: FAA AIM Para 5-4-20, Table 5-4-29 (image aim0504_Auto6.png).
// Transcribed 2026-05-17, cross-verified Codex × Sonnet 4 — all 30 cells
// matched.

/** AIM 5-4-29 unified circling radius table. Rows = MDA-MSL altitude band. */
export const CIRCLING_RADII_NM: Record<ApproachCategory, [number, number, number, number, number, number]> = {
  // bands:  [≤1000]  [1001-3000]  [3001-5000]  [5001-7000]  [7001-9000]  [≥9001]
  A: [1.3, 1.3, 1.3, 1.3, 1.4, 1.4],
  B: [1.7, 1.8, 1.8, 1.9, 2.0, 2.1],
  C: [2.7, 2.8, 2.9, 3.0, 3.2, 3.3],
  D: [3.6, 3.7, 3.8, 4.0, 4.2, 4.4],
  E: [4.5, 4.6, 4.8, 5.0, 5.3, 5.5],
};

/** Upper bounds (inclusive) for bands. Band 0: ≤1000 → upper 1000. Band 5: ≥9001 → upper Infinity. */
const BAND_UPPER_BOUNDS = [1000, 3000, 5000, 7000, 9000, Infinity];

/** Human-readable label for each band. Used for chip display. */
const BAND_LABELS = [
  '≤1000 ft MSL',
  '1001–3000 ft MSL',
  '3001–5000 ft MSL',
  '5001–7000 ft MSL',
  '7001–9000 ft MSL',
  '≥9001 ft MSL',
] as const;

/** Return the 0–5 band index for the given circling MDA (in feet MSL). */
export function circlingBandIndex(mdaMsl: number): number {
  for (let i = 0; i < BAND_UPPER_BOUNDS.length; i++) {
    if (mdaMsl <= BAND_UPPER_BOUNDS[i]) return i;
  }
  return 5; // unreachable; Infinity catches the rest
}

// ─── Main function ────────────────────────────────────────────────────────────

/**
 * Compute the circling radius for the given aircraft category + plate.
 *
 * @example
 * // CAT C at a low-elevation airport, MDA 920 ft MSL → band 0 (≤1000)
 * computeCirclingRadius({ category: 'C', circlingMdaFt: 920, airportElevFt: 257 })
 * // → { radiusNm: 2.7, bandLabel: '≤1000 ft MSL', agl: 663, isFallback: false }
 *
 * @example
 * // CAT A at KTEX (Telluride), MDA 12140 ft MSL, field 9078 → band 5 (≥9001)
 * computeCirclingRadius({ category: 'A', circlingMdaFt: 12140, airportElevFt: 9078 })
 * // → { radiusNm: 1.4, bandLabel: '≥9001 ft MSL', agl: 3062, isFallback: false }
 *
 * @example
 * // MDA unknown — under-claim by returning the lowest band's radius
 * computeCirclingRadius({ category: 'B', circlingMdaFt: null, airportElevFt: 257 })
 * // → { radiusNm: 1.7, bandLabel: '≤1000 ft MSL', agl: null, isFallback: true }
 */
export function computeCirclingRadius(input: CirclingRadiusInput): CirclingRadiusResult {
  const { category, circlingMdaFt, airportElevFt } = input;

  if (circlingMdaFt == null) {
    // Silent fallback. The previous warn fired on every recompute for
    // plates with category-missing minima — normal during picker churn /
    // basemap swaps — and added measurable devtools overhead with
    // devtools open. The `isFallback: true` return value already lets
    // callers surface a UI hint (radius chip + amber legend) without
    // needing a console warning. (Codex audit 2026-05-18, finding 10.)
    return {
      radiusNm: CIRCLING_RADII_NM[category][0],
      bandLabel: BAND_LABELS[0],
      agl: null,
      isFallback: true,
    };
  }

  const bandIdx = circlingBandIndex(circlingMdaFt);
  return {
    radiusNm: CIRCLING_RADII_NM[category][bandIdx],
    bandLabel: BAND_LABELS[bandIdx],
    agl: circlingMdaFt - airportElevFt,
    isFallback: false,
  };
}
